import { useState, useEffect, useRef } from 'react';
import { 
  PlayCircle, 
  ChevronLeft, 
  ChevronRight, 
  Maximize, 
  Minimize, 
  X, 
  Globe, 
  ChevronDown, 
  ChevronUp 
} from 'lucide-react';
import { useTranslation } from '../../../TranslationContext';

interface VideoTutorial {
  id: string;
  titleKey: string;
  descriptionKey: string;
  applicationType: string;
  videoUrlKey: string;
  thumbnailUrl: string;
  category: 'integration' | 'usage';
}

export const TutorialVideoSection = () => {
  const { t, currentLanguage } = useTranslation();
  const [hasCompany, setHasCompany] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoTutorial | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const scrollContainerRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  // Définition des tutoriels avec des clés de traduction
  const [videoTutorials] = useState<VideoTutorial[]>([
    // Integration tutorials
    {
      id: 'tuto-001',
      titleKey: 'tutorial_react_integration_title',
      descriptionKey: 'tutorial_react_integration_desc',
      applicationType: 'React',
      videoUrlKey: 'tutorial_react_integration_video',
      thumbnailUrl: '/thumbnails/react-integration.jpg',
      category: 'integration',
    },
    {
      id: 'tuto-002',
      titleKey: 'tutorial_nodejs_auth_title',
      descriptionKey: 'tutorial_nodejs_auth_desc',
      applicationType: 'Node.js',
      videoUrlKey: 'tutorial_nodejs_auth_video',
      thumbnailUrl: '/thumbnails/nodejs-auth.jpg',
      category: 'integration',
    },
    {
      id: 'tuto-003',
      titleKey: 'tutorial_angular_mfa_title',
      descriptionKey: 'tutorial_angular_mfa_desc',
      applicationType: 'Angular',
      videoUrlKey: 'tutorial_angular_mfa_video',
      thumbnailUrl: '/thumbnails/angular-mfa.jpg',
      category: 'integration',
    },
    {
      id: 'tuto-006',
      titleKey: 'tutorial_vue_integration_title',
      descriptionKey: 'tutorial_vue_integration_desc',
      applicationType: 'Vue.js',
      videoUrlKey: 'tutorial_vue_integration_video',
      thumbnailUrl: '/thumbnails/vue-integration.jpg',
      category: 'integration',
    },
    {
      id: 'tuto-007',
      titleKey: 'tutorial_flutter_integration_title',
      descriptionKey: 'tutorial_flutter_integration_desc',
      applicationType: 'Flutter',
      videoUrlKey: 'tutorial_flutter_integration_video',
      thumbnailUrl: '/thumbnails/flutter-integration.jpg',
      category: 'integration',
    },
    
    // Usage tutorials - organized by language groups
    {
      id: 'tuto-101',
      titleKey: 'tutorial_create_application',
      descriptionKey: 'tutorial_create_application_desc',
      applicationType: 'General',
      videoUrlKey: 'tutorial_create_application_video',
      thumbnailUrl: '/thumbnails/create-application.jpg',
      category: 'usage',
    },
    {
      id: 'tuto-102',
      titleKey: 'tutorial_add_user',
      descriptionKey: 'tutorial_add_user_desc',
      applicationType: 'General',
      videoUrlKey: 'tutorial_add_user_video',
      thumbnailUrl: '/thumbnails/add-user.jpg',
      category: 'usage',
    },
    {
      id: 'tuto-103',
      titleKey: 'tutorial_add_role',
      descriptionKey: 'tutorial_add_role_desc',
      applicationType: 'General',
      videoUrlKey: 'tutorial_add_role_video',
      thumbnailUrl: '/thumbnails/add-role.jpg',
      category: 'usage',
    },
    {
      id: 'tuto-104',
      titleKey: 'tutorial_user_management',
      descriptionKey: 'tutorial_user_management_desc',
      applicationType: 'General',
      videoUrlKey: 'tutorial_user_management_video',
      thumbnailUrl: '/thumbnails/user-management.jpg',
      category: 'usage',
    },
    {
      id: 'tuto-105',
      titleKey: 'tutorial_chart_usage',
      descriptionKey: 'tutorial_chart_usage_desc',
      applicationType: 'General',
      videoUrlKey: 'tutorial_chart_usage_video',
      thumbnailUrl: '/thumbnails/chart-usage.jpg',
      category: 'usage',
    },
  ]);

  useEffect(() => {
    const company = localStorage.getItem('company');
    if (company !== null) {
      setHasCompany(true);
    }
  }, []);

  const openVideoModal = (video: VideoTutorial) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setIsFullScreen(false);
  };
  
  const scroll = (containerIndex: number, direction: 'left' | 'right') => {
    const container = scrollContainerRefs.current[containerIndex];
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };
  
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!hasCompany) return null;

  const integrationTutorials = videoTutorials.filter(t => t.category === 'integration');
  const usageTutorials = videoTutorials.filter(t => t.category === 'usage');

  // Thumbnail sources for each tutorial - in a real implementation, these would be dynamic
  const getThumbnailSource = (tutorialId: string) => {
    const tutorialMap: Record<string, string> = {
      // Integration tutorials
      'tuto-001': t('tutorial_react_integration_thumbnail'),
      'tuto-002': t('tutorial_nodejs_auth_thumbnail'),
      'tuto-003': t('tutorial_angular_mfa_thumbnail'),
      'tuto-006': t('tutorial_vue_integration_thumbnail'),
      'tuto-007': t('tutorial_flutter_integration_thumbnail'),
      
      // Usage tutorials
      'tuto-101': t('tutorial_create_application_thumbnail'),
      'tuto-102': t('tutorial_add_user_thumbnail'),
      'tuto-103': t('tutorial_add_role_thumbnail'),
      'tuto-104': t('tutorial_user_management_thumbnail'),
      'tuto-105': t('tutorial_chart_usage_thumbnail'),
    };
    
    return tutorialMap[tutorialId] || '/api/placeholder/400/320';
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
      <div 
        className="flex justify-between items-center mb-6 cursor-pointer"
        onClick={toggleExpanded}
      >
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          {t('video_tutorials')}
          <span className="ml-2 flex items-center text-sm font-normal text-gray-500 dark:text-gray-400">
            <Globe className="h-4 w-4 mr-1" />
            {currentLanguage}
          </span>
        </h2>
        <button
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
          aria-label={isExpanded ? "Collapse section" : "Expand section"}
        >
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          )}
        </button>
      </div>

      {isExpanded && (
        <>
          {/* Integration Tutorials */}
          {integrationTutorials.length > 0 && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  {t('integration_tutorials')}
                </h3>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => scroll(0, 'left')}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                    aria-label="Scroll left"
                  >
                    <ChevronLeft className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                  </button>
                  <button 
                    onClick={() => scroll(0, 'right')}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                    aria-label="Scroll right"
                  >
                    <ChevronRight className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                  </button>
                </div>
              </div>
              
              <div 
                className="flex overflow-x-auto hide-scrollbar pb-4 -mx-2 px-2" 
                // ref={el => scrollContainerRefs.current[0] = el}
                ref={el => {
                  scrollContainerRefs.current[0] = el; // Assignez l'élément à la référence
              }}
              >
                <div className="flex space-x-6">
                  {integrationTutorials.map((tutorial) => (
                    <div
                      key={tutorial.id}
                      className="flex-shrink-0 w-72 bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105 hover:translate-y-px"
                    >
                      <div className="relative group">
                        <img
                          src={getThumbnailSource(tutorial.id)}
                          alt={`${t(tutorial.titleKey)} thumbnail`}
                          className="w-full h-40 object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-300">
                          <button
                            onClick={() => openVideoModal(tutorial)}
                            className="bg-indigo-600 text-white rounded-full p-3 transform scale-0 group-hover:scale-100 transition-all duration-300"
                            aria-label="Play video"
                          >
                            <PlayCircle className="h-6 w-6" />
                          </button>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 truncate">
                          {t(tutorial.titleKey)}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                          {t(tutorial.descriptionKey)}
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
                            {tutorial.applicationType}
                          </span>
                          <button
                            onClick={() => openVideoModal(tutorial)}
                            className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 text-sm flex items-center"
                          >
                            {t('watch_tutorial')} <PlayCircle className="h-4 w-4 ml-1" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Usage Tutorials */}
          {usageTutorials.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                  {t('usage_tutorials')}
                </h3>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => scroll(1, 'left')}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                    aria-label="Scroll left"
                  >
                    <ChevronLeft className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                  </button>
                  <button 
                    onClick={() => scroll(1, 'right')}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                    aria-label="Scroll right"
                  >
                    <ChevronRight className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                  </button>
                </div>
              </div>
              
              <div 
                className="flex overflow-x-auto hide-scrollbar pb-4 -mx-2 px-2" 
                // ref={el => scrollContainerRefs.current[1] = el}
                ref={el => {
                  scrollContainerRefs.current[0] = el; 
              }}
              >
                <div className="flex space-x-6">
                  {usageTutorials.map((tutorial) => (
                    <div
                      key={tutorial.id}
                      className="flex-shrink-0 w-72 bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105 hover:translate-y-px"
                    >
                      <div className="relative group">
                        <img
                          src={getThumbnailSource(tutorial.id)}
                          alt={`${t(tutorial.titleKey)} thumbnail`}
                          className="w-full h-40 object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-300">
                          <button
                            onClick={() => openVideoModal(tutorial)}
                            className="bg-indigo-600 text-white rounded-full p-3 transform scale-0 group-hover:scale-100 transition-all duration-300"
                            aria-label="Play video"
                          >
                            <PlayCircle className="h-6 w-6" />
                          </button>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 truncate">
                          {t(tutorial.titleKey)}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                          {t(tutorial.descriptionKey)}
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
                            {tutorial.applicationType}
                          </span>
                          <button
                            onClick={() => openVideoModal(tutorial)}
                            className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 text-sm flex items-center"
                          >
                            {t('watch_tutorial')} <PlayCircle className="h-4 w-4 ml-1" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center ${isFullScreen ? '' : 'bg-black bg-opacity-50'}`}>
          <div 
            className={`${
              isFullScreen 
                ? 'fixed inset-0 bg-black' 
                : 'bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl mx-4 shadow-xl'
            } p-6 relative transition-all duration-300`}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {t(selectedVideo.titleKey)}
              </h2>
              <div className="flex space-x-3">
                <button
                  onClick={toggleFullScreen}
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  aria-label={isFullScreen ? "Exit fullscreen" : "Enter fullscreen"}
                >
                  {isFullScreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
                </button>
                <button
                  onClick={closeVideoModal}
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {t(selectedVideo.descriptionKey)}
            </p>
            
            <div className={`rounded-lg overflow-hidden ${isFullScreen ? 'h-full' : ''}`}>
              <video
                controls
                className={`w-full ${isFullScreen ? 'h-[calc(100vh-200px)] object-contain' : 'rounded-lg'}`}
                src={t(selectedVideo.videoUrlKey)}
                autoPlay
              >
                {t('video_not_supported')}
              </video>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default TutorialVideoSection;

// import { useState, useEffect } from 'react';
// import { PlayCircle } from 'lucide-react';
// import { useTranslation } from '../../../TranslationContext';

// interface VideoTutorial {
//   id: string;
//   title: string;
//   description: string;
//   applicationType: string;
//   videoUrl: string;
//   thumbnailUrl: string;
//   category: 'integration' | 'usage';
// }

// export const TutorialVideoSection = () => {
//   const { t } = useTranslation();
//   const [hasCompany, setHasCompany] = useState(false);
//   const [selectedVideo, setSelectedVideo] = useState<VideoTutorial | null>(null);
//   const [videoTutorials] = useState<VideoTutorial[]>([
//     {
//       id: 'tuto-001',
//       title: t('tutorial_react_integration_title'),
//       description: t('tutorial_react_integration_desc'),
//       applicationType: 'React',
//       videoUrl: '/OtentiTutoReact.mp4',
//       thumbnailUrl: '/thumbnails/react-integration.jpg',
//       category: 'integration',
//     },
//     {
//       id: 'tuto-002',
//       title: t('tutorial_nodejs_auth_title'),
//       description: t('tutorial_nodejs_auth_desc'),
//       applicationType: 'Node.js',
//       videoUrl: '/videos/otenti-nodejs-auth.mp4',
//       thumbnailUrl: '/thumbnails/nodejs-auth.jpg',
//       category: 'integration',
//     },
//     {
//       id: 'tuto-003',
//       title: t('tutorial_angular_mfa_title'),
//       description: t('tutorial_angular_mfa_desc'),
//       applicationType: 'Angular',
//       videoUrl: '/videos/otenti-angular-mfa.mp4',
//       thumbnailUrl: '/thumbnails/angular-mfa.jpg',
//       category: 'integration',
//     },
//     {
//       id: 'tuto-004',
//       title: t('tutorial_user_management_title'),
//       description: t('tutorial_user_management_desc'),
//       applicationType: 'General',
//       videoUrl: '/OtentiTutoConnectApp.mp4',
//       thumbnailUrl: '/thumbnails/user-management.jpg',
//       category: 'usage',
//     },
//     {
//       id: 'tuto-005',
//       title: t('tutorial_dashboard_usage_title'),
//       description: t('tutorial_dashboard_usage_desc'),
//       applicationType: 'General',
//       videoUrl: '/videos/otenti-dashboard.mp4',
//       thumbnailUrl: '/thumbnails/dashboard-usage.jpg',
//       category: 'usage',
//     },
//   ]);

//   useEffect(() => {
//     const company = localStorage.getItem('company');
//     if (company !== null) {
//       setHasCompany(true);
//     }
//   }, []);

//   const openVideoModal = (video: VideoTutorial) => {
//     setSelectedVideo(video);
//   };

//   const closeVideoModal = () => {
//     setSelectedVideo(null);
//   };

//   if (!hasCompany) return null;

//   const integrationTutorials = videoTutorials.filter(t => t.category === 'integration');
//   const usageTutorials = videoTutorials.filter(t => t.category === 'usage');

//   return (
//     <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
//       <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
//         {t('video_tutorials')}
//       </h2>

//       {/* Integration Tutorials */}
//       {integrationTutorials.length > 0 && (
//         <>
//           <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
//             {t('integration_tutorials')}
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
//             {integrationTutorials.map((tutorial) => (
//               <div
//                 key={tutorial.id}
//                 className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 transform transition hover:scale-105"
//               >
//                 <img
//                   src='/CommentConnecter son App.PNG'
//                   alt={`${tutorial.title} thumbnail`}
//                   className="w-full h-40 object-cover rounded-lg mb-4"
//                 />
//                 <div className="flex items-center mb-4">
//                   <PlayCircle className="h-8 w-8 text-indigo-600 mr-3" />
//                   <h3 className="text-lg font-medium text-gray-900 dark:text-white">
//                     {tutorial.title}
//                   </h3>
//                 </div>
//                 <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
//                   {tutorial.description}
//                 </p>
//                 <div className="flex justify-between items-center">
//                   <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
//                     {tutorial.applicationType}
//                   </span>
//                   <button
//                     onClick={() => openVideoModal(tutorial)}
//                     className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 flex items-center"
//                   >
//                     {t('watch_tutorial')} <PlayCircle className="h-4 w-4 ml-2" />
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </>
//       )}

//       {/* Usage Tutorials */}
//       {usageTutorials.length > 0 && (
//         <>
//           <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
//             {t('usage_tutorials')}
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//             {usageTutorials.map((tutorial) => (
//               <div
//                 key={tutorial.id}
//                 className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 transform transition hover:scale-105"
//               >
//                 <img
//                   src='Otentitutominiature.PNG'
//                   alt={`${tutorial.title} thumbnail`}
//                   className="w-full h-40 object-cover rounded-lg mb-4"
//                 />
//                 <div className="flex items-center mb-4">
//                   <PlayCircle className="h-8 w-8 text-indigo-600 mr-3" />
//                   <h3 className="text-lg font-medium text-gray-900 dark:text-white">
//                     {tutorial.title}
//                   </h3>
//                 </div>
//                 <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
//                   {tutorial.description}
//                 </p>
//                 <div className="flex justify-between items-center">
//                   <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
//                     {tutorial.applicationType}
//                   </span>
//                   <button
//                     onClick={() => openVideoModal(tutorial)}
//                     className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 flex items-center"
//                   >
//                     {t('watch_tutorial')} <PlayCircle className="h-4 w-4 ml-2" />
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </>
//       )}

//       {/* Video Modal */}
//       {selectedVideo && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
//           <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl mx-4 p-6 relative">
//             <button
//               onClick={closeVideoModal}
//               className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
//             >
//               ✕
//             </button>
//             <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
//               {selectedVideo.title}
//             </h2>
//             <p className="text-gray-600 dark:text-gray-300 mb-4">
//               {selectedVideo.description}
//             </p>
//             <video
//               controls
//               className="w-full rounded-lg"
//               src={selectedVideo.videoUrl}
//             >
//               {t('video_not_supported')}
//             </video>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default TutorialVideoSection;

